import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type CreatePoolInput, type UpdatePoolInput, type Pool, type ApiResponse } from '@/services/api';

/**
 * Query key factory for pools
 */
export const poolKeys = {
  all: ['pools'] as const,
  lists: () => [...poolKeys.all, 'list'] as const,
  list: () => [...poolKeys.lists()] as const,
  details: () => [...poolKeys.all, 'detail'] as const,
  detail: (poolId: string) => [...poolKeys.details(), poolId] as const,
  members: (poolId: string) => [...poolKeys.detail(poolId), 'members'] as const,
};

/**
 * Hook to get all pools the current user is a member of
 */
export function useMyPools() {
  return useQuery({
    queryKey: poolKeys.list(),
    queryFn: async () => {
      const response = await api.membership.getMyPools();
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
  });
}

/**
 * Hook to get pool details by ID
 */
export function usePool(poolId: string) {
  return useQuery({
    queryKey: poolKeys.detail(poolId),
    queryFn: async () => {
      const response = await api.pools.getPool(poolId);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!poolId,
  });
}

/**
 * Hook to get pool members
 */
export function usePoolMembers(poolId: string) {
  return useQuery({
    queryKey: poolKeys.members(poolId),
    queryFn: async () => {
      const response = await api.pools.getPoolMembers(poolId);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!poolId,
  });
}

/**
 * Hook to create a new pool
 */
export function useCreatePool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePoolInput) => {
      const response = await api.pools.createPool(input);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
    },
  });
}

/**
 * Hook to update pool settings
 */
export function useUpdatePool(poolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdatePoolInput) => {
      const response = await api.pools.updatePool(poolId, input);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(poolId) });
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
    },
  });
}

/**
 * Hook to join a pool
 */
export function useJoinPool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteCode: string) => {
      const response = await api.membership.joinPool(inviteCode);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
    },
  });
}

/**
 * Hook to remove a member from a pool
 */
export function useRemoveMember(poolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.pools.removeMember(poolId, userId);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return { userId };
    },
    onSuccess: (data: { userId: string }) => {
      // Optimistically update the cache to immediately remove the member from the UI
      queryClient.setQueryData<import('@/services/api').MemberWithSubmissionStatus[]>(
        poolKeys.members(poolId),
        (oldData) => oldData?.filter((member) => member.userId !== data.userId)
      );

      // Also invalidate related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(poolId) });
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
    },
  });
}

/**
 * Hook to promote a member to cohost
 */
export function usePromoteToCohost(poolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.pools.promoteToCohost(poolId, userId);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.members(poolId) });
    },
  });
}

/**
 * Factory hook for pool action mutations (lock/unlock)
 */
function usePoolActionMutation(
  poolId: string,
  mutationFn: (poolId: string) => Promise<ApiResponse<Pool>>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await mutationFn(poolId);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(poolId) });
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
    },
  });
}

/**
 * Hook to lock a pool manually
 */
export function useLockPool(poolId: string) {
  return usePoolActionMutation(poolId, api.pools.lockPool);
}

/**
 * Hook to unlock a pool manually
 */
export function useUnlockPool(poolId: string) {
  return usePoolActionMutation(poolId, api.pools.unlockPool);
}
